import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getResultsSummary, getJobResults } from "../lib/api";
import { logEvent } from "../lib/log";
import Sidebar from "../components/Sidebar";
import DownloadModal from "../components/DownloadModal";

// ── Constants ─────────────────────────────────────────────────────────────────

const NAICS_SECTORS = {
  '11': 'Agriculture, Forestry & Fishing',
  '21': 'Mining, Quarrying & Oil/Gas',
  '22': 'Utilities',
  '23': 'Construction',
  '31': 'Manufacturing',
  '42': 'Wholesale Trade',
  '44': 'Retail Trade',
  '48': 'Transportation & Warehousing',
  '51': 'Information',
  '52': 'Finance & Insurance',
  '53': 'Real Estate & Rental',
  '54': 'Professional & Technical Services',
  '55': 'Management of Companies',
  '56': 'Administrative & Support Services',
  '61': 'Educational Services',
  '62': 'Health Care & Social Assistance',
  '71': 'Arts, Entertainment & Recreation',
  '72': 'Accommodation & Food Services',
  '81': 'Other Services',
  '92': 'Public Administration',
};

const ENRICHMENT_ROWS = [
  { key: 'names',    label: 'Company Names Cleaned',  statKey: 'names_cleaned',     color: '#0176CE', filter: 'names'    },
  { key: 'address',  label: 'Addresses Standardized', statKey: 'addresses_cleaned', color: '#F37428', filter: 'address'  },
  { key: 'domain',   label: 'Websites Found',         statKey: 'domains_found',     color: '#0176CE', filter: 'domain'   },
  { key: 'naics',    label: 'NAICS Codes Assigned',   statKey: 'naics_found',       color: '#10b981', filter: 'naics'    },
  { key: 'profile',  label: 'Business Profiles',      statKey: 'profiles_found',    color: '#F37428', filter: 'profile'  },
  { key: 'person',   label: 'People Identified',      statKey: 'people_found',      color: '#f59e0b', filter: 'person'   },
  { key: 'internal', label: 'Intercompany Flagged',   statKey: 'internal_found',    color: '#8b5cf6', filter: 'internal' },
  { key: 'error',    label: 'Errors',                 statKey: 'errors',            color: '#ef4444', filter: 'error'    },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(raw) {
  if (!raw) return "—";
  const d = new Date(raw);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function fmtDomainSource(v) {
  if (!v) return "";
  const map = {
    INPUT_DOMAIN_RAW:   "Input Domain (Verified)",
    SONNET_KNOWLEDGE:   "LLM2",
    WEB_SEARCH:         "LLM Search",
    NOT_FOUND:          "",
    ADDR_ONLY:          "",
  };
  if (v in map) return map[v];
  if (/haiku|step[\s_]?1/i.test(v)) return "LLM1";
  if (/sonnet|step[\s_]?2/i.test(v)) return "LLM2";
  if (/step[\s_]?[34]/i.test(v))     return "LLM Search";
  return "LLM";
}

function buildCSVRows(records, { includeDups = false } = {}) {
  const esc = (v) => {
    if (v == null) return "";
    const s = String(v);
    return s.includes(",") || s.includes('"') || s.includes("\n")
      ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const parseList = (v) => {
    if (!v) return "";
    try {
      const arr = typeof v === "string" ? JSON.parse(v) : v;
      return Array.isArray(arr) ? arr.join("; ") : String(v);
    } catch { return String(v); }
  };
  const digitsOnly = (v) => v ? String(v).replace(/\D/g, "") : "";

  const headers = [
    // SOURCE (raw import data)
    "System Name",
    "Vendor / Customer #",
    "Vendor Name",
    "Name Line 2",
    "Address Seq",
    "Address Line 1",
    "Address Line 2",
    "Address Line 3",
    "Address Line 4",
    "City",
    "State / Province",
    "ZIP / Postal Code",
    "Country",
    "Phone",
    "Email",
    "Tax ID",
    "Website / Domain",
    // AI ENRICHMENT
    "AI Vendor Name",
    "AI Name Line 2",
    "AI Address Line 1",
    "AI Address Line 2",
    "AI Address Line 3",
    "AI Address Line 4",
    "AI PO Box",
    "AI City",
    "AI State",
    "AI Postal Code",
    "AI Country",
    "AI Phone Formatted",
    "AI Phone E.164",
    "AI Phone Digits Only",
    "AI Email",
    "AI Tax ID",
    "AI Domain",
    "AI Domain Confidence %",
    "AI Domain Source",
    "AI NAICS Code",
    "AI NAICS Title",
    "AI NAICS Confidence %",
    "AI Alt NAICS 1 Code",
    "AI Alt NAICS 1 Title",
    "AI Alt NAICS 1 Confidence %",
    "AI Alt NAICS 2 Code",
    "AI Alt NAICS 2 Title",
    "AI Alt NAICS 2 Confidence %",
    "AI Business Description",
    "AI Key Offerings",
    "AI Industries Served",
    "AI Profile Confidence %",
    "AI Is Person",
    "AI Is Intercompany",
    "AI Enrichment Status",
    "AI Error Message",
    ...(includeDups ? ["Last Enriched", "Dup Domain", "Dup Address"] : []),
  ];

  const rows = records.map(r => [
    // SOURCE
    r.system_name,
    r.original_num,
    r.original_name,
    r.original_name2,
    r.address_seq || 1,
    r.original_address1,
    r.original_address2,
    r.original_address3,
    r.original_address4,
    r.original_city,
    r.original_state,
    r.original_postal,
    r.original_country,
    r.original_phone,
    r.original_email,
    r.original_taxid,
    r.original_domain,
    // AI ENRICHMENT
    r.company_name_recommended || r.name1_std,
    r.name2_std,
    r.address1_std,
    r.address2_std,
    r.address3_std,
    r.address4_std,
    r.po_box_std,
    r.city_std,
    r.state_std,
    r.postal_code_std,
    r.country_std,
    r.phone_formatted,
    r.phone_e164,
    digitsOnly(r.phone_e164 || r.phone_formatted),
    r.email_std,
    r.taxid_std,
    r.domain_value,
    r.domain_confidence,
    fmtDomainSource(r.domain_source),
    r.naics_code,
    r.naics_title,
    r.naics_confidence,
    r.naics_alt1_code,
    r.naics_alt1_title,
    r.naics_alt1_confidence,
    r.naics_alt2_code,
    r.naics_alt2_title,
    r.naics_alt2_confidence,
    r.what_they_do,
    parseList(r.key_offerings),
    parseList(r.industries_served),
    r.profile_confidence,
    r.is_person   ? "Yes" : "No",
    r.is_internal ? "Yes" : "No",
    r.ok ? "Enriched" : "Error",
    r.error_message,
    ...(includeDups ? [
      r.last_enriched_at ? new Date(r.last_enriched_at).toLocaleDateString("en-US") : "",
      r.dup_domain  || "",
      r.dup_address || "",
    ] : []),
  ].map(esc).join(","));

  return { headers, rows };
}

function downloadCSV(results, filename) {
  const { headers, rows } = buildCSVRows(results, { includeDups: false });

  const csv  = [headers.join(","), ...rows].join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = (filename || "results").replace(/\.[^.]+$/, "") + "_enriched.csv";
  a.click();
  URL.revokeObjectURL(url);
}

// ── Sub-components ────────────────────────────────────────────────────────────

function EnrichmentBar({ label, count, total, color, filter, jobId, navigate }) {
  const [hover, setHover] = useState(false);
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  const fillWidth = total > 0 ? Math.max(2, (count / total) * 100) : 0;

  return (
    <div
      style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "10px 0", cursor: "pointer",
        borderBottom: "1px solid #f1f3f8",
        background: hover ? "#fafbff" : "transparent",
        marginLeft: -16, marginRight: -16, paddingLeft: 16, paddingRight: 16,
        transition: "background 0.1s",
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => navigate(`/results/${jobId}/records?filter=${filter}`)}
    >
      <div style={{ width: 190, flexShrink: 0, color: "#374151", fontSize: 13, fontWeight: 500 }}>
        {label}
      </div>
      <div style={{ flex: 1, height: 8, background: "#e2e8f0", borderRadius: 4, overflow: "hidden" }}>
        <div style={{ width: `${fillWidth}%`, height: "100%", background: color, borderRadius: 4, transition: "width 0.4s ease" }} />
      </div>
      <div style={{ width: 50, textAlign: "right", color: "#374151", fontSize: 13, fontWeight: 600, flexShrink: 0 }}>
        {count.toLocaleString()}
      </div>
      <div style={{ width: 44, textAlign: "right", color: "#9ca3af", fontSize: 12, flexShrink: 0 }}>
        {pct}%
      </div>
      <div style={{ width: 16, color: hover ? "#0176CE" : "transparent", fontSize: 14, flexShrink: 0, textAlign: "right" }}>
        ›
      </div>
    </div>
  );
}

function NaicsBar({ sector, count, total, navigate, jobId }) {
  const [hover, setHover] = useState(false);
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  const fillWidth = total > 0 ? Math.max(2, (count / total) * 100) : 0;
  const isNull = sector === null;
  const label = isNull ? "Not classified" : `${sector} — ${NAICS_SECTORS[sector] || sector}`;
  const color = isNull ? "#d1d5db" : "#10b981";

  const handleClick = () => {
    if (isNull) {
      navigate(`/results/${jobId}/records?filter=unclassified`);
    } else {
      navigate(`/results/${jobId}/records?filter=naics_sector&sector=${sector}`);
    }
  };

  return (
    <div
      style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "10px 0", cursor: "pointer",
        borderBottom: "1px solid #f1f3f8",
        background: hover ? "#fafbff" : "transparent",
        marginLeft: -16, marginRight: -16, paddingLeft: 16, paddingRight: 16,
        transition: "background 0.1s",
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={handleClick}
    >
      <div style={{ width: 260, flexShrink: 0, color: isNull ? "#9ca3af" : "#374151", fontSize: 13, fontWeight: 500, fontStyle: isNull ? "italic" : "normal" }}>
        {label}
      </div>
      <div style={{ flex: 1, height: 8, background: "#e2e8f0", borderRadius: 4, overflow: "hidden" }}>
        <div style={{ width: `${fillWidth}%`, height: "100%", background: color, borderRadius: 4, transition: "width 0.4s ease" }} />
      </div>
      <div style={{ width: 50, textAlign: "right", color: "#374151", fontSize: 13, fontWeight: 600, flexShrink: 0 }}>
        {count.toLocaleString()}
      </div>
      <div style={{ width: 44, textAlign: "right", color: "#9ca3af", fontSize: 12, flexShrink: 0 }}>
        {pct}%
      </div>
      <div style={{ width: 16, color: hover ? "#0176CE" : "transparent", fontSize: 14, flexShrink: 0, textAlign: "right" }}>
        ›
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function Results() {
  const { jobId }  = useParams();
  const navigate   = useNavigate();

  const [data,         setData]        = useState(null);
  const [loading,      setLoading]     = useState(true);
  const [error,        setError]       = useState("");
  const [downloading,  setDownloading] = useState(false);
  const [showDownload, setShowDownload] = useState(false);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getResultsSummary(jobId);
      setData(res);
    } catch (e) {
      setError(e.message || "Could not load results.");
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => { fetchSummary(); logEvent('results_viewed', { job_id: jobId }); }, [fetchSummary]);

  async function handleDownload() {
    setDownloading(true);
    try {
      const PAGE_SIZE = 500;
      let page = 1;
      let allResults = [];
      let total = null;
      do {
        const res = await getJobResults(jobId, { page, limit: PAGE_SIZE });
        allResults = allResults.concat(res.results || []);
        if (total === null) total = res.total || 0;
        page++;
      } while (allResults.length < total && (allResults.length % PAGE_SIZE === 0));
      downloadCSV(allResults, data?.job?.file_name || `job_${jobId}`);
      setShowDownload(false);
    } catch (e) {
      alert("Download failed: " + e.message);
    } finally {
      setDownloading(false);
    }
  }

  const job      = data?.job;
  const stats    = data?.stats;
  const nav      = data?.nav;
  const naicsBreakdown = data?.naics_breakdown || [];
  const total    = stats?.total || 0;

  // Quality score color via gradient simulation
  const qs = stats?.quality_score ?? 0;
  const qsColor = qs >= 80 ? "#10b981" : qs >= 60 ? "#f59e0b" : "#ef4444";

  return (
    <div style={styles.page}>
      {showDownload && (
        <DownloadModal
          onClose={() => setShowDownload(false)}
          onDownload={handleDownload}
          downloading={downloading}
          vendorCount={job?.vendor_count ?? stats?.total}
          addressCount={job?.address_count}
          includeDups={false}
        />
      )}
      <div style={styles.topStripe} />
      <div style={styles.body}>
      <Sidebar active="dashboard" />

      {/* ── Main ────────────────────────────────────────────────────────────── */}
      <main style={styles.main}>

        {/* Header */}
        <div style={styles.headerRow}>
          <div>
            <Link to="/dashboard" style={styles.backLink}>← Back to dashboard</Link>
            <h1 style={styles.pageTitle}>Results</h1>
            {job && (
              <p style={styles.pageSub}>
                {job.file_name} · Job #{jobId} ·{" "}
                {job.vendor_count
                  ? <>{job.vendor_count.toLocaleString()} vendors{job.address_count > job.vendor_count ? `, ${job.address_count.toLocaleString()} addresses` : ""}</>
                  : <>{(job.record_count || 0).toLocaleString()} records</>
                } · Processed {formatDate(job.completed_on_utc || job.created_on_utc)}
              </p>
            )}
          </div>
          <div style={styles.headerActions}>
            {nav?.prev_job_id && (
              <button style={styles.navBtn} onClick={() => navigate(`/results/${nav.prev_job_id}`)}>
                ← Prev Job
              </button>
            )}
            {nav?.next_job_id && (
              <button style={styles.navBtn} onClick={() => navigate(`/results/${nav.next_job_id}`)}>
                Next Job →
              </button>
            )}
            <button style={styles.downloadBtn} onClick={() => setShowDownload(true)} disabled={!data}>
              ↓ Download CSV
            </button>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div style={styles.centerBox}>
            <div style={styles.spinner} />
            <p style={styles.mutedText}>Loading results…</p>
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div style={styles.errorCard}>
            <p style={{ color: "#f87171", margin: 0 }}>{error}</p>
            <button onClick={fetchSummary} style={styles.retryBtn}>Retry</button>
          </div>
        )}

        {!loading && !error && data && (
          <>
            {/* Two-column layout */}
            <div style={styles.twoCol}>

              {/* Left — Enrichment Results */}
              <div style={styles.card}>
                <h2 style={styles.cardTitle}>Enrichment Results</h2>
                <div style={{ marginTop: 8 }}>
                  {ENRICHMENT_ROWS.map(row => (
                    <EnrichmentBar
                      key={row.key}
                      label={row.label}
                      count={stats?.[row.statKey] || 0}
                      total={total}
                      color={row.color}
                      filter={row.filter}
                      jobId={jobId}
                      navigate={navigate}
                    />
                  ))}
                </div>
              </div>

              {/* Right — Quality Score */}
              <div style={styles.card}>
                <h2 style={styles.cardTitle}>Quality Score</h2>
                <div style={styles.qualityScoreBox}>
                  <div style={{ fontSize: 64, fontWeight: 800, color: qsColor, lineHeight: 1, letterSpacing: "-2px" }}>
                    {qs}%
                  </div>
                  <div style={{ color: "#9ca3af", fontSize: 13, marginTop: 8 }}>
                    Overall enrichment score
                  </div>
                </div>
                <div style={styles.divider} />
                <div style={styles.statsList}>
                  <div style={styles.statsListRow}>
                    <span style={styles.statsListLabel}>Enrichment Rate</span>
                    <span style={styles.statsListValue}>
                      {(() => {
                        const base = total - (stats?.people_found || 0) - (stats?.internal_found || 0);
                        return base > 0 ? `${Math.round(((stats?.enriched || 0) / base) * 100)}%` : "—";
                      })()}
                    </span>
                  </div>
                  <div style={styles.statsListRow}>
                    <span style={styles.statsListLabel}>Avg Domain Confidence</span>
                    <span style={styles.statsListValue}>
                      {stats?.avg_domain_confidence != null ? `${stats.avg_domain_confidence}%` : "—"}
                    </span>
                  </div>
                  <div style={styles.statsListRow}>
                    <span style={styles.statsListLabel}>Vendors Enriched</span>
                    <span style={styles.statsListValue}>
                      {(stats?.enriched || 0).toLocaleString()} / {Math.max(0, total - (stats?.people_found || 0) - (stats?.internal_found || 0)).toLocaleString()}
                    </span>
                  </div>
                  <div style={styles.statsListRow}>
                    <span style={styles.statsListLabel}>Errors</span>
                    <span style={{ ...styles.statsListValue, color: stats?.errors > 0 ? "#ef4444" : "#9ca3af" }}>
                      {(stats?.errors || 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* NAICS Industry Breakdown */}
            {naicsBreakdown.length > 0 && (
              <div style={{ ...styles.card, marginTop: 24 }}>
                <h2 style={styles.cardTitle}>Industry Breakdown (NAICS)</h2>
                <p style={styles.cardSub}>Click any sector to view those records</p>
                <div style={{ marginTop: 8 }}>
                  {naicsBreakdown.map((item, i) => (
                    <NaicsBar
                      key={item.sector ?? "null"}
                      sector={item.sector}
                      count={item.count}
                      total={total}
                      navigate={navigate}
                      jobId={jobId}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </main>
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const styles = {
  page:         { display: "flex", flexDirection: "column", minHeight: "100vh", background: "#f8fafc", fontFamily: "'Inter', system-ui, sans-serif" },
  topStripe:    { height: 4, background: "linear-gradient(90deg, #0176CE, #F37428)", flexShrink: 0 },
  body:         { display: "flex", flex: 1 },
  main:         { flex: 1, padding: "40px 48px", overflowY: "auto" },
  headerRow:    { display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 28 },
  backLink:     { color: "#6b7280", fontSize: 13, textDecoration: "none", display: "block", marginBottom: 8 },
  pageTitle:    { color: "#111827", fontSize: 28, fontWeight: 700, margin: 0, letterSpacing: "-0.5px" },
  pageSub:      { color: "#6b7280", fontSize: 14, margin: "4px 0 0" },
  headerActions:{ display: "flex", gap: 10, alignItems: "center", flexShrink: 0, marginTop: 6 },
  navBtn:       { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, color: "#374151", cursor: "pointer", fontSize: 13, fontWeight: 500, padding: "9px 16px" },
  downloadBtn:  { background: "#F37428", border: "none", borderRadius: 8, color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600, padding: "9px 18px", whiteSpace: "nowrap" },
  centerBox:    { textAlign: "center", padding: "80px 24px", display: "flex", flexDirection: "column", alignItems: "center" },
  mutedText:    { color: "#6b7280", marginTop: 16, fontSize: 14 },
  spinner:      { width: 32, height: 32, border: "3px solid #e2e8f0", borderTop: "3px solid #0176CE", borderRadius: "50%", animation: "spin 0.8s linear infinite" },
  errorCard:    { background: "#fff", border: "1px solid #fecaca", borderRadius: 12, padding: "32px 24px", textAlign: "center" },
  retryBtn:     { marginTop: 12, background: "transparent", border: "1px solid #0176CE", borderRadius: 6, color: "#0176CE", cursor: "pointer", fontSize: 13, padding: "7px 16px" },
  twoCol:       { display: "grid", gridTemplateColumns: "65fr 35fr", gap: 24 },
  card:         { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "24px 16px" },
  cardTitle:    { color: "#111827", fontSize: 16, fontWeight: 700, margin: "0 0 4px", letterSpacing: "-0.3px" },
  cardSub:      { color: "#9ca3af", fontSize: 13, margin: "0 0 8px" },
  qualityScoreBox: { textAlign: "center", padding: "24px 0 20px" },
  divider:      { borderTop: "1px solid #e2e8f0", margin: "16px 0" },
  statsList:    { display: "flex", flexDirection: "column", gap: 12 },
  statsListRow: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  statsListLabel:{ color: "#6b7280", fontSize: 13 },
  statsListValue:{ color: "#111827", fontSize: 13, fontWeight: 600 },
};
